import { ChevronDown } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import { Input } from "@/components/ui/input"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Switch } from "@/components/ui/switch"
import { cn } from "@/lib/utils"

// IMP-388: the reworked business-hours editor, shared by the Business profile
// settings view and the onboarding details step. It carries an overarching
// status (mirroring Google Business Profile) above the weekly grid; only
// "Open with main hours" shows the schedule. A previously entered schedule is
// preserved when a status hides it and reappears on switching back, because the
// day array is kept independently of the status.

export type HoursStatus =
  | "open_with_hours"
  | "open_no_hours"
  | "temporarily_closed"
  | "permanently_closed"

// A type alias rather than an interface so hours payloads stay assignable to
// Inertia's FormDataConvertible, which needs an index signature.
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type DayHours = {
  day: string
  abbr: string
  open: boolean
  // Open 24 hours — from/to are ignored. `all_day?` is optional so callers with
  // legacy data (and the admin account form) stay assignable; treated as false.
  all_day?: boolean
  from: string
  to: string
}

export const HOURS_MODES: {
  value: HoursStatus
  label: string
  description: string
}[] = [
  {
    value: "open_with_hours",
    label: "Open with main hours",
    description: "Show your weekly schedule",
  },
  {
    value: "open_no_hours",
    label: "Open, hours not set",
    description: "You're open, but don't show specific hours",
  },
  {
    value: "temporarily_closed",
    label: "Temporarily closed",
    description: "Show that your business will open again in the future",
  },
  {
    value: "permanently_closed",
    label: "Permanently closed",
    description: "Show that your business no longer exists",
  },
]

export const DEFAULT_HOURS: DayHours[] = [
  { day: "Monday", abbr: "Mon", open: true, all_day: false, from: "09:00", to: "17:00" }, // prettier-ignore
  { day: "Tuesday", abbr: "Tue", open: true, all_day: false, from: "09:00", to: "17:00" }, // prettier-ignore
  { day: "Wednesday", abbr: "Wed", open: true, all_day: false, from: "09:00", to: "17:00" }, // prettier-ignore
  { day: "Thursday", abbr: "Thu", open: true, all_day: false, from: "09:00", to: "17:00" }, // prettier-ignore
  { day: "Friday", abbr: "Fri", open: true, all_day: false, from: "09:00", to: "17:00" }, // prettier-ignore
  { day: "Saturday", abbr: "Sat", open: true, all_day: false, from: "10:00", to: "14:00" }, // prettier-ignore
  { day: "Sunday", abbr: "Sun", open: false, all_day: false, from: "10:00", to: "14:00" }, // prettier-ignore
]

const ALL_DAY_LABEL = "24 hours"

// 30-minute time options ("00:00".."23:30") with 12-hour labels, matching how
// Google Business Profile presents its open/close pickers.
const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const hour = Math.floor(i / 2)
  const minute = i % 2 === 0 ? "00" : "30"
  const value = `${String(hour).padStart(2, "0")}:${minute}`
  const period = hour < 12 ? "AM" : "PM"
  const hour12 = hour % 12 === 0 ? 12 : hour % 12
  return { value, label: `${hour12}:${minute} ${period}` }
})

// Both pickers lead with "24 hours"; choosing it on either field makes the whole
// day open 24 hours.
const TIME_MENU = [
  { value: ALL_DAY_LABEL, label: ALL_DAY_LABEL },
  ...TIME_OPTIONS,
]

// Format a stored value (an "HH:MM" 24h string, or the all-day sentinel) into
// the 12-hour label shown in the field.
function formatTime(value: string): string {
  if (value === ALL_DAY_LABEL) return ALL_DAY_LABEL
  const m = /^(\d{2}):(\d{2})$/.exec(value)
  if (!m) return value
  const hour = Number(m[1])
  const period = hour < 12 ? "AM" : "PM"
  const hour12 = hour % 12 === 0 ? 12 : hour % 12
  return `${hour12}:${m[2]} ${period}`
}

// Parse free text into a stored value, or null if it isn't a valid time.
// Accepts "9", "9:15", "9 am", "9:15 PM", "14:30", and (when allowed) "24
// hours". Off-grid times (not on the 30-minute marks) are allowed.
function parseTime(text: string, allowAllDay: boolean): string | null {
  const t = text.trim().toLowerCase()
  if (allowAllDay && /^24(\s*(h|hr|hrs|hours))?$/.test(t)) return ALL_DAY_LABEL

  const ampm = /^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/.exec(t)
  if (ampm) {
    let hour = Number(ampm[1])
    const minute = ampm[2] ? Number(ampm[2]) : 0
    if (hour < 1 || hour > 12 || minute > 59) return null
    if (ampm[3] === "pm" && hour !== 12) hour += 12
    if (ampm[3] === "am" && hour === 12) hour = 0
    return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`
  }

  const h24 = /^(\d{1,2}):(\d{2})$/.exec(t)
  if (h24) {
    const hour = Number(h24[1])
    const minute = Number(h24[2])
    if (hour > 23 || minute > 59) return null
    return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`
  }
  return null
}

function dayRange(h: DayHours): string {
  if (h.all_day) return ALL_DAY_LABEL
  return `${formatTime(h.from)} – ${formatTime(h.to)}`
}

// Collapsed-row / read-only summary of the current status + schedule. Groups
// consecutive open days that share hours (e.g. "Mon–Fri 9:00 AM – 5:00 PM").
export function hoursSummary(status: HoursStatus, hours: DayHours[]): string {
  if (status === "open_no_hours") return "Open, hours not set"
  if (status === "temporarily_closed") return "Temporarily closed"
  if (status === "permanently_closed") return "Permanently closed"

  const open = hours.filter((d) => d.open)
  if (open.length === 0) return "Closed all week"

  const groups: { days: DayHours[]; key: string }[] = []
  for (const day of open) {
    const key = dayRange(day)
    const last = groups[groups.length - 1]
    if (last?.key === key) last.days.push(day)
    else groups.push({ days: [day], key })
  }
  return groups
    .map((g) => {
      const days =
        g.days.length === 1
          ? g.days[0].abbr
          : `${g.days[0].abbr}–${g.days[g.days.length - 1].abbr}`
      return `${days} ${g.key}`
    })
    .join(", ")
}

// A GBP-style time field: an always-editable input plus a dropdown that always
// shows the FULL 30-minute list (never filtered by what's typed). Typing edits a
// local draft and highlights the matching mark; picking a mark sets it. Only
// valid times commit — an invalid entry marks the field and, on blur, reverts to
// the value it held before the edit (matching how GBP behaves).
function TimeCombobox({
  value,
  allowAllDay = false,
  onValueChange,
}: {
  value: string
  allowAllDay?: boolean
  onValueChange: (value: string) => void
}) {
  const committedLabel = formatTime(value)
  const [text, setText] = useState(committedLabel)
  const [open, setOpen] = useState(false)
  const activeRef = useRef<HTMLButtonElement>(null)

  // Resync the draft when the committed value changes underneath us (e.g. the
  // parent resets the schedule, or a sibling field flips the day to 24 hours).
  useEffect(() => {
    setText(formatTime(value))
  }, [value])

  useEffect(() => {
    if (open) activeRef.current?.scrollIntoView({ block: "center" })
  }, [open])

  const parsed = parseTime(text, allowAllDay)
  const invalid = parsed === null

  function commitOrRevert() {
    if (parsed !== null) {
      onValueChange(parsed)
      setText(formatTime(parsed))
    } else {
      setText(committedLabel)
    }
    setOpen(false)
  }

  function pick(optValue: string) {
    onValueChange(optValue)
    setText(formatTime(optValue))
    setOpen(false)
  }

  return (
    <div className="relative flex-1">
      <Input
        aria-label="Time"
        aria-invalid={invalid}
        value={text}
        onFocus={() => setOpen(true)}
        onChange={(e) => setText(e.target.value)}
        onBlur={commitOrRevert}
        className="h-8 pr-7 tabular-nums"
      />
      <button
        type="button"
        tabIndex={-1}
        aria-label="Show times"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((v) => !v)}
        className="text-muted-foreground absolute top-1/2 right-2 -translate-y-1/2"
      >
        <ChevronDown className="size-4" />
      </button>
      {open && (
        <div className="bg-popover absolute top-full right-0 left-0 z-50 mt-1 max-h-56 overflow-y-auto rounded-md border p-1 shadow-md">
          {TIME_MENU.map((opt) => {
            const active = opt.label === text
            return (
              <button
                key={opt.value}
                ref={active ? activeRef : undefined}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(opt.value)}
                className={cn(
                  "hover:bg-muted flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-sm tabular-nums",
                  active && "bg-muted font-medium",
                )}
              >
                {opt.label}
                {active && (
                  <span className="text-muted-foreground text-xs">✓</span>
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// The overarching status selector plus the weekly schedule (rendered only for
// "Open with main hours"). Stateless — the caller owns `status` + `hours` so the
// schedule survives status changes that hide it.
export function HoursEditor({
  status,
  hours,
  onStatusChange,
  onHourChange,
}: {
  status: HoursStatus
  hours: DayHours[]
  onStatusChange: (status: HoursStatus) => void
  onHourChange: (idx: number, patch: Partial<DayHours>) => void
}) {
  return (
    <div className="space-y-3">
      <RadioGroup
        value={status}
        onValueChange={(v) => onStatusChange(v as HoursStatus)}
        className="bg-background gap-0 divide-y overflow-hidden rounded-lg border"
      >
        {HOURS_MODES.map((mode) => (
          <label
            key={mode.value}
            htmlFor={`hours-${mode.value}`}
            className="hover:bg-muted/40 flex cursor-pointer items-start gap-3 px-3 py-3"
          >
            <RadioGroupItem
              id={`hours-${mode.value}`}
              value={mode.value}
              className="mt-0.5"
            />
            <div className="grid gap-0.5">
              <span className="text-sm font-medium">{mode.label}</span>
              <span className="text-muted-foreground text-sm">
                {mode.description}
              </span>
            </div>
          </label>
        ))}
      </RadioGroup>

      {status === "open_with_hours" && (
        <WeeklyHoursGrid hours={hours} onHourChange={onHourChange} />
      )}
    </div>
  )
}

// The weekly open/close schedule grid — a switch per day plus from/to time
// fields. Extracted so it can be reused on its own (IMP-431: the Lead Responder's
// transfer hours), independent of the business-profile status selector above.
//
//   - allowAllDay: offer the "24 hours" option (default on). Callers whose
//     backend has no 24h concept (transfer hours) pass false.
//   - closedLabel: the text shown for a day that's switched off ("Closed" for
//     business hours, "Unavailable" for transfer availability).
export function WeeklyHoursGrid({
  hours,
  onHourChange,
  allowAllDay = true,
  closedLabel = "Closed",
}: {
  hours: DayHours[]
  onHourChange: (idx: number, patch: Partial<DayHours>) => void
  allowAllDay?: boolean
  closedLabel?: string
}) {
  return (
    <div className="bg-background divide-y rounded-lg border">
      {hours.map((h, i) => (
        <div key={h.day} className="flex items-center gap-3 px-3 py-2.5">
          <span className="text-muted-foreground w-10 shrink-0 text-sm font-medium">
            {h.abbr}
          </span>
          <Switch
            size="sm"
            checked={h.open}
            onCheckedChange={(v) => onHourChange(i, { open: v })}
          />
          {h.open ? (
            <div className="flex flex-1 items-center gap-2">
              <TimeCombobox
                allowAllDay={allowAllDay}
                value={h.all_day ? ALL_DAY_LABEL : h.from}
                onValueChange={(v) =>
                  v === ALL_DAY_LABEL
                    ? onHourChange(i, { all_day: true })
                    : onHourChange(i, { all_day: false, from: v })
                }
              />
              {!h.all_day && (
                <>
                  <span className="text-muted-foreground shrink-0 text-xs">
                    to
                  </span>
                  <TimeCombobox
                    allowAllDay={allowAllDay}
                    value={h.to}
                    onValueChange={(v) =>
                      v === ALL_DAY_LABEL
                        ? onHourChange(i, { all_day: true })
                        : onHourChange(i, { to: v })
                    }
                  />
                </>
              )}
            </div>
          ) : (
            <span className="text-muted-foreground flex-1 text-sm italic">
              {closedLabel}
            </span>
          )}
        </div>
      ))}
    </div>
  )
}
